import { showToast } from '@features/common/store/common.reducer';
import { getFieldValidations } from '@features/common/store/common.selectors';
import LoadingIndicator from '@features/layout/components/LoadingIndicator';
import { parseError } from '@http/httpHelpers';
import { AsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch } from '@store/store';
import Link from 'next/link';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import InputText from '../../common/components/InputText';
import PrimaryButton from '../../common/components/PrimaryButton';
import { ILoginForm } from '../models/login-form.model';
import AuthFormWrapper from './AuthFormWrapper';

interface ISendEmailProps {
	action: AsyncThunk<any, any, any>;
	title: string;
	successMessage: string;
	description: string;
	isLoading: boolean;
	link?: {
		text: string;
		linkText: string;
		path: string;
	};
}

const SendEmail = ({ action, title, successMessage, description, link, isLoading }: ISendEmailProps) => {
	const dispatch = useAppDispatch();
	const { formatMessage } = useIntl();
	const { email } = useSelector(getFieldValidations);
	const { register, handleSubmit, formState } = useForm<Pick<ILoginForm, 'email'>>({ mode: 'onBlur' });

	const onSubmit = (formValue: Pick<ILoginForm, 'email'>) => {
		dispatch(action(formValue))
			.then(unwrapResult)
			.then(() =>
				dispatch(
					showToast({
						message: successMessage,
						type: 'success',
						title: formatMessage({ id: 'bcs-Common-Nice' }),
						buttonText: formatMessage({ id: 'bcs-Common-GotIt' }),
					})
				)
			)
			.catch((error) => {
				const thrownError = parseError(error);
				dispatch(
					showToast({
						message: thrownError.message,
						type: 'warning',
						title: formatMessage({ id: 'bcs-Common-Oops' }),
					})
				);
			});
	};

	if (isLoading) {
		return (
			<LoadingIndicator
				className={'bg-white md:bg-black flex flex-col justify-center items-center w-full h-full'}
				contained
			/>
		);
	}

	return (
		<AuthFormWrapper pathForBack='/login-email' title={title} description={description}>
			<form className='mt-10' onSubmit={handleSubmit(onSubmit)} data-testid='form'>
				<InputText
					id={'email'}
					label={formatMessage({ id: 'bcs-Auth-Email' })}
					type={'email'}
					testId={'email'}
					tabIndex={1}
					fieldError={formState.errors.email}
					placeholder={formatMessage({ id: 'bcs-Auth-Email' })}
					hookRegister={register('email', {
						required: {
							value: true,
							message: formatMessage({
								id: 'bcs-Error-EmptyEmail',
							}),
						},
						pattern: {
							value: RegExp(email),
							message: formatMessage({
								id: 'bcs-Error-InvalidEmail',
							}),
						},
					})}
				/>

				<div className='mt-6'>
					<PrimaryButton
						type='submit'
						text={formatMessage({ id: 'bcs-Common-Send' })}
						tabIndex={3}
						testId="sendEmailBtn"
						disabled={isLoading}
					/>
				</div>
				{link ? (
					<div
						className='flex font-medium text-xs justify-center'
						style={{
							fontFamily: "'Inter', sans-serif",
							marginTop: '27px',
						}}
					>
						<p>{link.text}</p>
						<Link href={link.path}>
							<span className='font-medium text-xs text-main-500 transition-all cursor-pointer hover:text-main-600 ml-2'>
								{link.linkText}
							</span>
						</Link>
					</div>
				) : null}
			</form>
		</AuthFormWrapper>
	);
};

export default SendEmail;
